import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import i18n from '../i18n';
import { pageTitle } from '../utils/pageTitle';
import { canOpenPath } from '../utils/routeAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Title stored with the visit (QuickActions shows it in the viewer's
// language from the path again; this is only the fallback).
const getPageTitle = (path) => {
  const title = pageTitle(i18n.t.bind(i18n), path);
  if (title) return title;
  const lastPart = path.split('/').pop() || i18n.t('shell.pageTracker.defaultTitle');
  return lastPart.charAt(0).toUpperCase() + lastPart.slice(1).replace(/-/g, ' ');
};

// `enabled: false` turns tracking off (the superuser has no tenant
// dashboard whose quick actions would use the visits). With `user`, a
// visit to a screen the user cannot open ("Erişim Yetkiniz Yok") is not
// counted (P2-22: denied pages became dashboard shortcuts).
const usePageTracker = ({ enabled = true, user = null } = {}) => {
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;
    const trackPageVisit = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Skip tracking for auth pages and landing
      const skipPaths = ['/', '/login', '/forgot-password', '/reset-password', '/supplier'];
      if (skipPaths.some(path => location.pathname === path || location.pathname.startsWith('/supplier'))) {
        return;
      }

      // Get base path without dynamic params
      const basePath = location.pathname.replace(/\/[a-f0-9-]{36}$/, '').replace(/\/\d+$/, '');
      if (user && !canOpenPath(user, basePath)) return;
      const title = getPageTitle(basePath);

      try {
        await axios.post(
          `${API_URL}/api/page-visits`,
          { path: basePath, title },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug('Page tracking failed:', error);
      }
    };

    trackPageVisit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, enabled]);
};

export default usePageTracker;
